import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { LoyaltyDashboardLoadedUseCase } from '~use-cases/LoyaltyDashboardLoadedUseCase';
import { LoadLoyaltyInfoUseCase } from '~use-cases/LoadLoyaltyInfoUseCase';
import { ILoyaltyLayouts } from '~views/components/interfaces';
import { LoyaltyLandingConfig } from '~exported/loyalty-landing-page/ILoyaltyLandingPage';
import { createMultiGridLayout } from '~utils/loadOnDemand';
import mergeConfigurationFiles from '~utils/mergeConfigurationFiles';
import { ISiteConfig, IDrupalConfig, ILoyaltyConfig } from '~domain/entities/ISiteConfig';

export interface ILoyaltyDashboardController {
    loyaltyRepository: LoyaltyRepository;
    loadConfig(): Promise<void>;
}

class LoyaltyDashboardController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    @observable private configs: LoyaltyLandingConfig;
    @observable private layouts: ILoyaltyLayouts;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        await this.loadConfig();
        await new LoyaltyDashboardLoadedUseCase().execute();
        await new LoadLoyaltyInfoUseCase().execute();
        await this.loadBirthdayOffers();
        this.isLoading = false;
    };

    @action
    public async loadConfig(): Promise<void> {
        try {
            const loyaltyConfig = (window?.site as ISiteConfig)?.loyaltyConfig?.loyalty;

            const [drupalConfigData, loyaltyConfigData] = await Promise.all([
                import(`../../config/${loyaltyConfig.domain}-drupal.json`),
                import(`../../config/${loyaltyConfig.domain}-loyalty.json`)
            ]) as [IDrupalConfig, ILoyaltyConfig];

            const mergedConfig = mergeConfigurationFiles(
                drupalConfigData.drupal?.dashboard?.loyaltyLandingPageConfig || {},
                loyaltyConfigData?.dashboard?.loyaltyLandingPageConfig || {}
            );

            this.setInitialConfigs(mergedConfig);
        } catch (error) {
            console.error('Failed to load loyalty config:', error);
        }
    }

    @action
    setInitialConfigs(configs: LoyaltyLandingConfig) {
        this.configs = configs;
        this.layouts = createMultiGridLayout(configs);
    }

    @action
    private async loadBirthdayOffers(): Promise<void> {
        const hasBirthdaySection = Boolean(this.configs?.birthday);

        if (!hasBirthdaySection) {
            return;
        }

        const query = this.configs.birthday?.params?.birthday?.birthdayOfferType?.text;

        if (!query) {
            console.warn('Birthday section enabled but no query type found in config');

            return;
        }

        try {
            await this.loyaltyRepository.fetchBirthdayData(query);
        } catch (error) {
            console.error('Failed to fetch birthday data:', error);
        }
    }

    @computed
    public get data() {
        return {
            isLoading: this.isLoading,
            isLoyaltyUser: this.loyaltyRepository?.loyaltyInfo?.hasLoyalty,
            layouts: this.layouts,
            configs: this.configs,
            hasEligibleBirthdayOffers: this.loyaltyRepository?.hasEligibleBirthdayOffers
        };
    }
}
export default LoyaltyDashboardController;
